import React, { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import DataTable, { Column } from '@/components/reusable/DataTable';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { shopifyApi, formatCurrency } from '@/lib/api/services/shopify';
import { toast } from '@/hooks/use-toast';
import { friendlyMessage, showApiError } from '@/lib/errors';
import { showListSkeleton, useFirstLoad } from '@/lib/loadingState';
import { Search, X, Trash2, Loader2, Pencil, MoreHorizontal, RefreshCw, Plus, MoreVertical } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { EditShopifyProductDialog } from './EditShopifyProductDialog';
import { CreateShopifyProductDialog } from './CreateShopifyProductDialog';
import { useUrlParams } from '@/hooks/useUrlState';

interface ShopifyProductsProps {
    selectedAccountId: string | null;
    isConnected: boolean;
    shopCurrency: string;
    refreshKey?: number;
    hideToolbar?: boolean;
}

export const ShopifyProducts: React.FC<ShopifyProductsProps> = ({
    selectedAccountId,
    isConnected,
    shopCurrency,
    refreshKey = 0,
    hideToolbar = false,
}) => {
    const [searchParams] = useSearchParams();
    const { set: setUrl } = useUrlParams();

    const [localProducts, setLocalProducts] = useState<any[]>([]);
    const [totalProducts, setTotalProducts] = useState(0);
    const [loading, setLoading] = useState(false);
    const { hasLoaded, markLoaded } = useFirstLoad();
    const [error, setError] = useState<string | null>(null);
    const [fetchKey, setFetchKey] = useState(0);

    const [deleteProductDialogOpen, setDeleteProductDialogOpen] = useState(false);
    const [productToDelete, setProductToDelete] = useState<{ id: string; shopify_product_id: string; title: string } | null>(null);
    const [deleteProductLoading, setDeleteProductLoading] = useState(false);

    const [editProductDialogOpen, setEditProductDialogOpen] = useState(false);
    const [productToEdit, setProductToEdit] = useState<any | null>(null);
    const [createProductDialogOpen, setCreateProductDialogOpen] = useState(false);
    const [syncLoading, setSyncLoading] = useState(false);

    const page = parseInt(searchParams.get('shopProdPage') || '1');
    const setPage = (p: number) => setUrl({ shopProdPage: p > 1 ? String(p) : '' });

    const pageSize = parseInt(searchParams.get('shopProdPageSize') || '10');
    const setPageSize = (s: number) =>
        setUrl({ shopProdPageSize: s !== 10 ? String(s) : '', shopProdPage: '' });

    const search = searchParams.get('shopProdSearch') || '';
    const [searchInput, setSearchInput] = useState(search);

    useEffect(() => {
        setSearchInput(prev => (prev === search ? prev : search));
    }, [search]);

    useEffect(() => {
        if (searchInput === search) return;
        const t = setTimeout(() => {
            setUrl({ shopProdSearch: searchInput, shopProdPage: '' }, { replace: true });
        }, 400);
        return () => clearTimeout(t);
    }, [searchInput, search, setUrl]);

    const setSearch = (value: string) => {
        setSearchInput(value);
    };

    const statusFilter = searchParams.get('shopProdStatus') || 'all';
    const setStatusFilter = (value: string) =>
        setUrl({ shopProdStatus: value === 'all' ? '' : value, shopProdPage: '' }, { replace: true });

    const vendorFilter = searchParams.get('shopProdVendor') || 'all';
    const setVendorFilter = (value: string) =>
        setUrl({ shopProdVendor: value === 'all' ? '' : value, shopProdPage: '' }, { replace: true });

    const typeFilter = searchParams.get('shopProdType') || 'all';
    const setTypeFilter = (value: string) =>
        setUrl({ shopProdType: value === 'all' ? '' : value, shopProdPage: '' }, { replace: true });

    const sortBy = searchParams.get('shopProdSort') || 'title';
    const setSortBy = (value: string) =>
        setUrl({ shopProdSort: value === 'title' ? '' : value }, { replace: true });

    const sortOrder = (searchParams.get('shopProdOrder') as 'asc' | 'desc') || 'asc';
    const setSortOrder = (value: 'asc' | 'desc') =>
        setUrl({ shopProdOrder: value === 'asc' ? '' : value }, { replace: true });

    useEffect(() => {
        const targetId = selectedAccountId;
        if (!isConnected || !targetId) return;
        const offset = (page - 1) * pageSize;
        setLoading(true);
        setError(null);
        shopifyApi
            .getLocalProducts(targetId, pageSize, offset, {
                search: search || undefined,
                status: statusFilter,
                vendor: vendorFilter,
                type: typeFilter,
            })
            .then((res) => {
                setLocalProducts(Array.isArray(res.products) ? res.products : []);
                setTotalProducts(res.total ?? 0);
            })
            .catch((e: any) => setError(friendlyMessage(e, "We couldn't load your products. Please try again.")))
            .finally(() => { setLoading(false); markLoaded(); });
    }, [isConnected, selectedAccountId, refreshKey, fetchKey, page, pageSize, search, statusFilter, vendorFilter, typeFilter]);

    const loadLocalProducts = () => setFetchKey(k => k + 1);



    const handleDeleteProduct = async () => {
        if (!selectedAccountId || !productToDelete) return;
        setDeleteProductLoading(true);
        try {
            await shopifyApi.deleteProduct(selectedAccountId, productToDelete.shopify_product_id);
            toast({ variant: "success", title: 'Product Deleted', description: 'Product deleted from Shopify.' });
            setDeleteProductDialogOpen(false);
            setProductToDelete(null);
            loadLocalProducts();
        } catch (e: any) {
            showApiError(e, { fallback: { title: 'Delete Failed' } });
        } finally {
            setDeleteProductLoading(false);
        }
    };

    const openDeleteDialog = (product: any) => {
        setProductToDelete({
            id: product.id,
            shopify_product_id: product.shopify_product_id,
            title: product.title || 'Untitled',
        });
        setDeleteProductDialogOpen(true);
    };

    const openEditDialog = (product: any) => {
        setProductToEdit(product);
        setEditProductDialogOpen(true);
    };

    const handleSyncProducts = async () => {
        if (!selectedAccountId) {
            toast({ title: 'Business Account Required', description: 'Please select a business account to sync products.', variant: 'warning' });
            return;
        }
        setSyncLoading(true);
        try {
            const res = await shopifyApi.syncProducts(selectedAccountId);
            if (res.status === 'queued') {
                toast({ variant: "success", title: 'Sync Started', description: res.message || 'Products sync started in the background.' });
            } else {
                const count = res.synced || 0;
                toast(count > 0
                    ? { title: 'Products Synced', description: `Synced ${count} ${count === 1 ? 'product' : 'products'} from Shopify.` }
                    : { title: 'Up to date', description: 'No new products to sync from Shopify.' }
                );
                loadLocalProducts();
            }
        } catch (e: any) {
            showApiError(e, { fallback: { title: 'Sync Failed' } });
        } finally {
            setSyncLoading(false);
        }
    };

    const uniqueVendors = useMemo(() => {
        const vendors = new Set(localProducts.map(p => p.vendor).filter(Boolean));
        return Array.from(vendors).sort();
    }, [localProducts]);

    const uniqueTypes = useMemo(() => {
        const types = new Set(localProducts.map(p => p.product_type).filter(Boolean));
        return Array.from(types).sort();
    }, [localProducts]);

    const filteredAndSortedProducts = useMemo(() => {
        const result = [...localProducts];

        result.sort((a, b) => {
            let comparison = 0;
            switch (sortBy) {
                case 'title':
                    comparison = (a.title || '').localeCompare(b.title || '');
                    break;
                case 'price':
                    const priceA = parseFloat(a.variants?.[0]?.price || '0');
                    const priceB = parseFloat(b.variants?.[0]?.price || '0');
                    comparison = priceA - priceB;
                    break;
                case 'vendor':
                    comparison = (a.vendor || '').localeCompare(b.vendor || '');
                    break;
                case 'status':
                    comparison = (a.status || '').localeCompare(b.status || '');
                    break;
                default:
                    comparison = 0;
            }
            return sortOrder === 'asc' ? comparison : -comparison;
        });

        return result;
    }, [localProducts, sortBy, sortOrder]);

    const columns: Column<any>[] = useMemo(() => [
        {
            key: 'image',
            header: 'Image',
            render: (_, product) => {
                let images = product.images;
                if (typeof images === 'string') {
                    try { images = JSON.parse(images); } catch { images = []; }
                }
                if (!Array.isArray(images)) images = [];
                const img = images.length > 0 ? images[0].src : null;
                return (
                    <div className="w-10 h-10 rounded bg-muted flex items-center justify-center overflow-hidden">
                        {img ? (
                            <img src={img} alt={product.title} className="w-full h-full object-cover" />
                        ) : (
                            <div className="text-muted-foreground opacity-50 text-xs">IMG</div>
                        )}
                    </div>
                );
            },
            className: 'w-12',
        },
        {
            key: 'product',
            header: 'Product',
            render: (_, product) => (
                <div>
                    <div className="font-medium text-sm truncate max-w-[200px]">{product.title}</div>
                    {product.handle && <div className="text-xs text-muted-foreground truncate max-w-[200px]">/{product.handle}</div>}
                </div>
            ),
        },
        {
            key: 'vendor',
            header: 'Vendor',
            render: (_, p) => <span className="text-sm text-muted-foreground">{p.vendor || '-'}</span>,
            className: 'hidden sm:table-cell',
            headerClassName: 'hidden sm:table-cell',
        },
        {
            key: 'type',
            header: 'Type',
            render: (_, p) => <span className="text-sm text-muted-foreground">{p.product_type || '-'}</span>,
            className: 'hidden md:table-cell',
            headerClassName: 'hidden md:table-cell',
        },
        {
            key: 'price',
            header: 'Price',
            render: (_, p) => {
                let variants = p.variants;
                if (typeof variants === 'string') try { variants = JSON.parse(variants); } catch { }
                const price = variants?.[0]?.price;
                return <span className="text-right font-medium text-sm">{price ? formatCurrency(parseFloat(price), shopCurrency) : '-'}</span>;
            },
            className: 'text-right',
            headerClassName: 'text-right',
        },
        {
            key: 'stock',
            header: 'Stock',
            render: (_, p) => {
                let variants = p.variants;
                if (typeof variants === 'string') try { variants = JSON.parse(variants); } catch { variants = []; }
                if (!Array.isArray(variants)) variants = [];
                const total = variants.reduce((sum: number, v: any) => sum + (Number(v.inventory_quantity) || 0), 0);
                return <span className="text-right text-sm">{total}</span>;
            },
            className: 'text-right hidden sm:table-cell',
            headerClassName: 'text-right hidden sm:table-cell',
        },
        {
            key: 'status',
            header: 'Status',
            render: (_, p) => <Badge variant={p.status === 'active' ? 'default' : 'secondary'} className='text-xs'>{p.status}</Badge>
        },
        {
            key: 'actions',
            header: 'Actions',
            render: (_, p) => (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                            <span className="sr-only">Open menu</span>
                            <MoreVertical className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openEditDialog(p); }}>
                            <Pencil className="mr-2 h-4 w-4" />
                            Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openDeleteDialog(p); }} className="text-destructive focus:text-destructive">
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            ),
            className: 'w-10'
        }
    ], [shopCurrency]);

    return (
        <div className="space-y-4">
            {error && <div className="p-3 bg-destructive/10 text-destructive rounded text-sm">{error}</div>}

            <div className="flex flex-col sm:flex-row flex-wrap gap-3 items-center justify-between">
                <div className="flex flex-1 gap-2 items-center w-full sm:w-auto">
                    <div className="relative flex-1 sm:max-w-[300px]">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Search products..."
                            value={searchInput}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-9 h-9"
                        />
                        {searchInput && <X className="absolute right-2.5 top-2.5 h-4 w-4 text-muted-foreground cursor-pointer" onClick={() => setSearch('')} />}
                    </div>

                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-[120px] h-9">
                            <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Status</SelectItem>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="draft">Draft</SelectItem>
                            <SelectItem value="archived">Archived</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {!hideToolbar && (
                    <div className="flex gap-2 items-center">
                        <Button size="sm" variant="outline" onClick={handleSyncProducts} disabled={syncLoading || !selectedAccountId}>
                            {syncLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Syncing...</> : <><RefreshCw className="mr-2 h-4 w-4" />Sync from Shopify</>}
                        </Button>
                        <Button size="sm" onClick={() => setCreateProductDialogOpen(true)} disabled={!selectedAccountId}>
                            <Plus className="mr-2 h-4 w-4" />Create Product
                        </Button>
                    </div>
                )}
            </div>

            <div className="rounded-md border">
                <DataTable
                    columns={columns}
                    data={filteredAndSortedProducts}
                    currentPage={page}
                    pageSize={pageSize}
                    totalItems={totalProducts}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    loading={showListSkeleton({ loading, hasLoaded, willFetch: isConnected && !!selectedAccountId })}
                    emptyMessage="No products found."
                    showPagination={true}
                />
            </div>

            <CreateShopifyProductDialog
                open={createProductDialogOpen}
                onOpenChange={setCreateProductDialogOpen}
                selectedAccountId={selectedAccountId}
                shopCurrency={shopCurrency}
                onSuccess={loadLocalProducts}
            />

            <EditShopifyProductDialog
                open={editProductDialogOpen}
                onOpenChange={setEditProductDialogOpen}
                product={productToEdit}
                selectedAccountId={selectedAccountId}
                shopCurrency={shopCurrency}
                onSuccess={loadLocalProducts}
            />

            <AlertDialog open={deleteProductDialogOpen} onOpenChange={setDeleteProductDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Product</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete "{productToDelete?.title}"? This action cannot be undone and will permanently delete the product from Shopify.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleteProductLoading}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteProduct}
                            disabled={deleteProductLoading}
                            className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
                        >
                            {deleteProductLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Deleting...</> : 'Delete'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};
